/**
 * MarkdownView - renders generated prompt text as cleanly formatted content
 * (headings, bold, lists) instead of showing raw markdown meta tags like ### and **.
 *
 * The underlying source string is preserved unchanged for copy/save actions —
 * this only changes how it is *displayed*.
 */
import { Box, Divider, Link, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import ReactMarkdown from 'react-markdown';

interface MarkdownViewProps {
  children: string;
  /** Optional sx applied to the wrapping Box. */
  sx?: SxProps<Theme>;
  /** Use smaller body2 typography for dense surfaces (e.g. dialogs). */
  dense?: boolean;
}

export default function MarkdownView({ children, sx, dense = false }: MarkdownViewProps) {
  const bodyVariant = dense ? 'body2' : 'body1';
  return (
    <Box sx={{ wordBreak: 'break-word', ...sx }}>
      <ReactMarkdown
        components={{
          h1: ({ children }) => (
            <Typography variant="h5" fontWeight={700} sx={{ mt: 2, mb: 1 }}>{children}</Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>{children}</Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>{children}</Typography>
          ),
          h4: ({ children }) => (
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>{children}</Typography>
          ),
          h5: ({ children }) => (
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, mb: 0.5 }}>{children}</Typography>
          ),
          h6: ({ children }) => (
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, mb: 0.5 }}>{children}</Typography>
          ),
          p: ({ children }) => (
            <Typography variant={bodyVariant} sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{children}</Typography>
          ),
          strong: ({ children }) => (
            <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>
          ),
          em: ({ children }) => (
            <Box component="em" sx={{ fontStyle: 'italic' }}>{children}</Box>
          ),
          ul: ({ children }) => (
            <Box component="ul" sx={{ pl: 3, mb: 1, mt: 0 }}>{children}</Box>
          ),
          ol: ({ children }) => (
            <Box component="ol" sx={{ pl: 3, mb: 1, mt: 0 }}>{children}</Box>
          ),
          li: ({ children }) => (
            <Box component="li" sx={{ mb: 0.5 }}>
              <Typography variant={bodyVariant} component="span">{children}</Typography>
            </Box>
          ),
          blockquote: ({ children }) => (
            <Box
              component="blockquote"
              sx={{
                borderLeft: '4px solid',
                borderColor: 'divider',
                pl: 2,
                ml: 0,
                my: 1,
                color: 'text.secondary',
              }}
            >
              {children}
            </Box>
          ),
          code: ({ children }) => (
            <Box
              component="code"
              sx={{
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                bgcolor: 'action.hover',
                fontFamily: 'monospace',
                fontSize: '0.875em',
              }}
            >
              {children}
            </Box>
          ),
          pre: ({ children }) => (
            <Box
              component="pre"
              sx={{
                p: 1.5,
                my: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
                overflowX: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.85em',
                whiteSpace: 'pre-wrap',
              }}
            >
              {children}
            </Box>
          ),
          hr: () => <Divider sx={{ my: 2 }} />,
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer">{children}</Link>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}
